import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { profileAPI, UserOnboardingProgress } from '@/lib/supabase/profile';
import { User, Settings, Award, CheckCircle, Clock, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Import step components
import { ProfileSetupStep } from '../components/ProfileSetupStep';
import { PermissionsReviewStep } from '../components/PermissionsReviewStep';
import { TrainingStep } from '../components/TrainingStep';
import { ManagerApprovalStep } from '../components/ManagerApprovalStep';
import { ActivationStep } from '../components/ActivationStep';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  component: React.ComponentType<any>;
  required: boolean;
  estimatedTime: string;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'profile_setup',
    title: 'Profile Setup',
    description: 'Complete your personal and professional information',
    icon: <User className='h-5 w-5' />,
    component: ProfileSetupStep,
    required: true,
    estimatedTime: '5 minutes'
  },
  {
    id: 'permissions_review',
    title: 'Permissions Review',
    description: 'Review your access levels and permissions',
    icon: <Settings className='h-5 w-5' />,
    component: PermissionsReviewStep,
    required: true,
    estimatedTime: '3 minutes'
  },
  {
    id: 'training',
    title: 'Training & Orientation',
    description: 'Complete required training modules',
    icon: <Award className='h-5 w-5' />,
    component: TrainingStep,
    required: true,
    estimatedTime: '15 minutes'
  },
  {
    id: 'manager_approval',
    title: 'Manager Approval',
    description: 'Await manager review and approval',
    icon: <Users className='h-5 w-5' />,
    component: ManagerApprovalStep,
    required: true,
    estimatedTime: 'Varies'
  },
  {
    id: 'activation',
    title: 'Account Activation',
    description: 'Final activation and welcome',
    icon: <CheckCircle className='h-5 w-5' />,
    component: ActivationStep,
    required: true,
    estimatedTime: '2 minutes'
  }
];

export const OnboardingFlow: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [progress, setProgress] = useState<UserOnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadProgress();
    }
  }, [user?.id]);

  const loadProgress = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const progressData = await profileAPI.getOnboardingProgress(user.id);

      if (progressData) {
        setProgress(progressData);
        // Find current step index
        const stepIndex = ONBOARDING_STEPS.findIndex(step => step.id === progressData.current_step);
        setCurrentStepIndex(stepIndex >= 0 ? stepIndex : 0);
      } else {
        // Initialize progress for new user
        await profileAPI.updateOnboardingProgress(user.id, {
          current_step: 'profile_setup',
          completed_steps: [],
          approval_status: 'pending'
        });
        setCurrentStepIndex(0);
      }
    } catch (error) {
      toast.error('Failed to load onboarding progress');
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (stepId: string) => {
    if (!progress) return 'pending';

    if (progress.completed_steps.includes(stepId)) return 'completed';
    if (progress.skipped_steps.includes(stepId)) return 'skipped';
    if (progress.current_step === stepId) return 'current';
    return 'pending';
  };

  const getProgressPercentage = () => {
    if (!progress) return 0;
    return Math.round((progress.completed_steps.length / ONBOARDING_STEPS.length) * 100);
  };

  const handleStepComplete = async (stepId: string) => {
    if (!user?.id) return;

    try {
      await profileAPI.completeOnboardingStep(user.id, stepId);

      // Move to next step
      const nextStepIndex = currentStepIndex + 1;
      if (nextStepIndex < ONBOARDING_STEPS.length) {
        setCurrentStepIndex(nextStepIndex);
        await profileAPI.updateOnboardingProgress(user.id, {
          current_step: ONBOARDING_STEPS[nextStepIndex]?.id || 'completed'
        });
      } else {
        // Onboarding complete
        await profileAPI.updateOnboardingProgress(user.id, {
          current_step: 'completed',
          activation_completed_at: new Date().toISOString()
        });
        toast.success('Onboarding completed successfully!');
        navigate('/');
      }

      // Reload progress
      await loadProgress();
    } catch (error) {
      toast.error('Failed to complete step');
    }
  };

  const handleStepSkip = async (stepId: string) => {
    if (!user?.id) return;

    try {
      const currentProgress = progress;
      if (currentProgress) {
        const skippedSteps = [...(currentProgress.skipped_steps || []), stepId];
        await profileAPI.updateOnboardingProgress(user.id, {
          skipped_steps: skippedSteps
        });

        // Move to next step
        const nextStepIndex = currentStepIndex + 1;
        if (nextStepIndex < ONBOARDING_STEPS.length) {
          setCurrentStepIndex(nextStepIndex);
          await profileAPI.updateOnboardingProgress(user.id, {
            current_step: ONBOARDING_STEPS[nextStepIndex]?.id || 'completed'
          });
        }

        await loadProgress();
      }
    } catch (error) {
      toast.error('Failed to skip step');
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleNextStep = () => {
    if (currentStepIndex < ONBOARDING_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto' />
          <p className='mt-2 text-muted-foreground'>Loading onboarding...</p>
        </div>
      </div>
    );
  }

  const currentStep = ONBOARDING_STEPS[currentStepIndex];
  const CurrentStepComponent = currentStep?.component;
  const progressPercentage = getProgressPercentage();

  return (
    <div className='min-h-screen bg-background'>
      {/* Header */}
      <div className='border-b bg-card'>
        <div className='container mx-auto px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-2xl font-bold'>Welcome to Vehix</h1>
              <p className='text-muted-foreground'>Let's get you set up</p>
            </div>
            <div className='text-right'>
              <p className='text-sm text-muted-foreground'>
                Step {currentStepIndex + 1} of {ONBOARDING_STEPS.length}
              </p>
              <p className='text-sm font-medium'>{progressPercentage}% Complete</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className='mt-4'>
            <Progress value={progressPercentage} className='h-2' />
          </div>
        </div>
      </div>

      <div className='container mx-auto px-6 py-8'>
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-8'>
          {/* Steps Sidebar */}
          <div className='lg:col-span-1'>
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>Onboarding Steps</CardTitle>
              </CardHeader>
              <CardContent className='space-y-2'>
                {ONBOARDING_STEPS.map((step, index) => {
                  const status = getStepStatus(step.id);
                  const isCurrent = index === currentStepIndex;

                  return (
                    <div
                      key={step.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        isCurrent ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setCurrentStepIndex(index)}
                    >
                      <div
                        className={`flex-shrink-0 ${
                          status === 'completed'
                            ? 'text-green-600'
                            : status === 'current'
                              ? 'text-primary'
                              : 'text-muted-foreground'
                        }`}
                      >
                        {status === 'completed' ? (
                          <CheckCircle className='h-5 w-5' />
                        ) : status === 'skipped' ? (
                          <Clock className='h-5 w-5' />
                        ) : (
                          step.icon
                        )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p
                          className={`text-sm font-medium ${
                            isCurrent ? 'text-primary' : 'text-foreground'
                          }`}
                        >
                          {step.title}
                        </p>
                        <p className='text-xs text-muted-foreground'>{step.estimatedTime}</p>
                      </div>
                      {status === 'completed' && (
                        <Badge variant='secondary' className='text-xs'>
                          Done
                        </Badge>
                      )}
                      {status === 'skipped' && (
                        <Badge variant='outline' className='text-xs'>
                          Skipped
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className='lg:col-span-3'>
            <Card>
              <CardHeader>
                <div className='flex items-center gap-3'>
                  <div className='text-primary'>{currentStep?.icon}</div>
                  <div>
                    <CardTitle>{currentStep?.title}</CardTitle>
                    <CardDescription>{currentStep?.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {CurrentStepComponent && (
                  <CurrentStepComponent
                    onComplete={() => handleStepComplete(currentStep?.id || '')}
                    onSkip={() => handleStepSkip(currentStep?.id || '')}
                    onPrevious={handlePreviousStep}
                    onNext={handleNextStep}
                    canGoBack={currentStepIndex > 0}
                    canGoForward={currentStepIndex < ONBOARDING_STEPS.length - 1}
                    isRequired={currentStep?.required || false}
                    estimatedTime={currentStep?.estimatedTime || ''}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
